import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MdCheck, MdClose, MdExpandMore, MdSearch } from "react-icons/md";
import { normalizeFormSelectOptions } from "lib/selectOptions";
import { UI_DROPDOWN_MENU, UI_LABEL, UI_SELECT } from "lib/uiClasses";

export type SelectOption = {
  value: string | number;
  label: string;
  disabled?: boolean;
  meta?: React.ReactNode;
};

export type SelectProps = {
  label?: React.ReactNode;
  placeholder?: string;
  value: string | number | null | undefined;
  onChange: (next: string | number | null) => void;
  options: SelectOption[];
  disabled?: boolean;
  loading?: boolean;
  error?: string | boolean;
  helperText?: string;
  required?: boolean;
  searchable?: boolean;
  clearable?: boolean;
  dir?: "rtl" | "ltr";
  /** Convenience: sets dir and default empty/loading copy */
  isArabic?: boolean;
  className?: string;
  id?: string;
  /** Coerce selection to number; cleared value uses `emptyValue` */
  numeric?: boolean;
  emptyValue?: number | string | null;
  /** When false, no clear row and selection is required */
  allowEmpty?: boolean;
  /** Portal dropdown to document.body (recommended inside modals). Default true. */
  portal?: boolean;
};

const DROPDOWN_Z = 1400;

function isEmptyValue(value: unknown): boolean {
  return value === null || value === undefined || value === "";
}

function valuesMatch(a: unknown, b: unknown): boolean {
  if (isEmptyValue(a) && isEmptyValue(b)) return true;
  return String(a) === String(b);
}

function useDropdownPosition(
  open: boolean,
  triggerRef: React.RefObject<HTMLButtonElement | null>,
  menuRef: React.RefObject<HTMLDivElement | null>,
  portal: boolean
) {
  const [style, setStyle] = useState<React.CSSProperties | null>(null);

  const update = useCallback(() => {
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const menuHeight = menuRef.current?.offsetHeight ?? 280;
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;

    const top = openUp ? rect.top - menuHeight - 4 : rect.bottom + 4;
    const left = rect.left;
    const width = rect.width;

    setStyle(
      portal
        ? {
            position: "fixed",
            top: Math.max(8, Math.min(top, window.innerHeight - menuHeight - 8)),
            left: Math.max(8, Math.min(left, window.innerWidth - width - 8)),
            width,
            zIndex: DROPDOWN_Z,
          }
        : {
            position: "absolute",
            top: openUp ? undefined : "100%",
            bottom: openUp ? "100%" : undefined,
            left: 0,
            right: 0,
            marginTop: openUp ? undefined : 4,
            marginBottom: openUp ? 4 : undefined,
            zIndex: DROPDOWN_Z,
          }
    );
  }, [portal, triggerRef, menuRef]);

  useEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    update();
    const onScrollOrResize = () => update();
    window.addEventListener("resize", onScrollOrResize);
    window.addEventListener("scroll", onScrollOrResize, true);
    return () => {
      window.removeEventListener("resize", onScrollOrResize);
      window.removeEventListener("scroll", onScrollOrResize, true);
    };
  }, [open, update]);

  return style;
}

export default function Select({
  label,
  placeholder,
  value,
  onChange,
  options,
  disabled = false,
  loading = false,
  error,
  helperText,
  required = false,
  searchable = false,
  clearable,
  dir,
  isArabic = false,
  className = "",
  id: idProp,
  numeric = false,
  emptyValue = null,
  allowEmpty = true,
  portal = true,
}: SelectProps) {
  const autoId = useId();
  const id = idProp ?? autoId;
  const labelId = `${id}-label`;
  const listboxId = `${id}-listbox`;
  const helperId = `${id}-helper`;
  const errorId = `${id}-error`;

  const resolvedDir = dir ?? (isArabic ? "rtl" : "ltr");

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [highlightIndex, setHighlightIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const safeOptions = useMemo(() => {
    const normalized = normalizeFormSelectOptions(options);
    const sourceByValue = new Map(options.map((o) => [String(o.value), o]));
    return normalized.map((o) => {
      const source = sourceByValue.get(String(o.value));
      return {
        ...o,
        disabled: source?.disabled ?? false,
        meta: source?.meta,
      };
    });
  }, [options]);

  const matched = useMemo(
    () =>
      safeOptions.find((o) => {
        if (isEmptyValue(value)) return false;
        return valuesMatch(o.value, value);
      }),
    [safeOptions, value]
  );

  const defaultPlaceholder = placeholder ?? (isArabic ? "اختر..." : "Select...");
  const canClear = clearable ?? allowEmpty;

  const filtered = useMemo(() => {
    if (!searchable) return safeOptions;
    const q = query.trim().toLowerCase();
    if (!q) return safeOptions;
    return safeOptions.filter(
      (o) =>
        o.label.toLowerCase().includes(q) || String(o.value).toLowerCase().includes(q)
    );
  }, [safeOptions, query, searchable]);

  const selectableOptions = useMemo(
    () => filtered.filter((o) => !o.disabled),
    [filtered]
  );

  const dropdownStyle = useDropdownPosition(open, triggerRef, menuRef, portal);

  const emit = useCallback(
    (raw: string | number | null) => {
      if (raw === null || raw === "") {
        if (numeric) {
          onChange(emptyValue ?? null);
        } else {
          onChange(allowEmpty ? (emptyValue ?? null) : value ?? null);
        }
        return;
      }
      if (numeric) {
        const num = Number(raw);
        onChange(Number.isFinite(num) ? num : emptyValue ?? null);
      } else {
        onChange(raw);
      }
    },
    [numeric, emptyValue, allowEmpty, onChange, value]
  );

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    setHighlightIndex(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled || loading) return;
    setOpen(true);
    const idx = selectableOptions.findIndex((o) => valuesMatch(o.value, value));
    setHighlightIndex(idx >= 0 ? idx : 0);
  }, [disabled, loading, selectableOptions, value]);

  const selectAt = useCallback(
    (index: number) => {
      const opt = selectableOptions[index];
      if (!opt) return;
      emit(opt.value);
      close();
      triggerRef.current?.focus();
    },
    [selectableOptions, emit, close]
  );

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (containerRef.current?.contains(target)) return;
      if (menuRef.current?.contains(target)) return;
      close();
    };
    const t = setTimeout(() => document.addEventListener("mousedown", onClick), 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener("mousedown", onClick);
    };
  }, [open, close]);

  useEffect(() => {
    if (open && searchable) {
      requestAnimationFrame(() => searchRef.current?.focus());
    }
  }, [open, searchable]);

  const onTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (disabled || loading) return;

    if (!open) {
      if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        openMenu();
      }
      return;
    }

    if (e.key === "Escape") {
      e.preventDefault();
      close();
      return;
    }

    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightIndex((i) => Math.min(i + 1, selectableOptions.length - 1));
      return;
    }

    if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightIndex((i) => Math.max(i - 1, 0));
      return;
    }

    if (e.key === "Enter") {
      e.preventDefault();
      if (highlightIndex >= 0) selectAt(highlightIndex);
      return;
    }

    if (e.key === "Tab") {
      close();
    }
  };

  const hasError = Boolean(error);
  const errorMessage = typeof error === "string" ? error : undefined;
  const showPlaceholder = !matched;

  const emptyListLabel = isArabic ? "لا توجد اختيارات" : "No options";
  const loadingLabel = isArabic ? "جاري التحميل..." : "Loading...";
  const noResultsLabel = isArabic ? "لا توجد نتائج" : "No results";

  const triggerClasses = [
    UI_SELECT,
    "flex items-center gap-2 text-start font-medium shadow-sm hover:border-brand-400/50",
    hasError ? "border-red-500 focus:border-red-500 focus:ring-red-500/20" : "",
    open ? "border-brand-500 ring-2 ring-brand-500/20" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  const dropdown = open ? (
    <div
      ref={menuRef}
      id={listboxId}
      role="listbox"
      dir={resolvedDir}
      className={`overflow-hidden text-start ${UI_DROPDOWN_MENU} ${
        portal ? "" : "absolute mt-1 w-full"
      }`}
      style={portal ? dropdownStyle ?? undefined : dropdownStyle ?? undefined}
    >
      {searchable ? (
        <div className="flex items-center gap-2 border-b border-gray-100 px-2 py-2 dark:border-white/[0.08]">
          <MdSearch className="h-4 w-4 shrink-0 text-gray-400 dark:text-slate-500" aria-hidden />
          <input
            ref={searchRef}
            type="text"
            role="searchbox"
            inputMode="search"
            autoComplete="off"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setHighlightIndex(0);
            }}
            placeholder={isArabic ? "ابحث..." : "Search..."}
            className="min-w-0 flex-1 appearance-none border-0 bg-transparent py-1 text-start text-sm text-gray-800 outline-none placeholder:text-gray-400 dark:text-slate-50 dark:placeholder:text-slate-500"
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
                e.preventDefault();
                onTriggerKeyDown(e as unknown as React.KeyboardEvent);
              }
              if (e.key === "Escape") {
                e.preventDefault();
                close();
                triggerRef.current?.focus();
              }
            }}
          />
        </div>
      ) : null}

      <ul className="max-h-60 overflow-y-auto py-1" role="presentation">
        {loading ? (
          <li className="px-3 py-4 text-center text-xs text-gray-500 dark:text-slate-400" role="option" aria-selected={false}>
            {loadingLabel}
          </li>
        ) : (
          <>
            {canClear && allowEmpty ? (
              <li role="presentation">
                <button
                  type="button"
                  role="option"
                  aria-selected={isEmptyValue(value)}
                  className="flex w-full items-center gap-2 px-3 py-2.5 text-start text-sm text-gray-500 hover:bg-gray-50 dark:hover:bg-white/10"
                  onClick={() => {
                    emit(null);
                    close();
                  }}
                >
                  {defaultPlaceholder}
                </button>
              </li>
            ) : null}

            {filtered.map((opt) => {
              const selectableIdx = selectableOptions.findIndex((o) =>
                valuesMatch(o.value, opt.value)
              );
              const selected = valuesMatch(opt.value, value);
              const highlighted = selectableIdx === highlightIndex && !opt.disabled;

              return (
                <li key={String(opt.value)} role="presentation">
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    disabled={opt.disabled}
                    className={`flex w-full items-center gap-2 px-3 py-2.5 text-start text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                      highlighted
                        ? "bg-brand-50 dark:bg-brand-500/15"
                        : selected
                        ? "bg-brand-50/70 font-semibold text-brand-700 dark:bg-brand-500/10 dark:text-brand-200"
                        : "text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-white/10"
                    }`}
                    onMouseEnter={() => {
                      if (!opt.disabled && selectableIdx >= 0) setHighlightIndex(selectableIdx);
                    }}
                    onClick={() => {
                      if (opt.disabled) return;
                      emit(opt.value);
                      close();
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{opt.label}</span>
                    {opt.meta ? <span className="shrink-0 text-xs text-gray-500">{opt.meta}</span> : null}
                    {selected ? (
                      <MdCheck
                        className="ms-auto h-4 w-4 shrink-0 text-brand-600 dark:text-brand-300"
                        aria-hidden
                      />
                    ) : (
                      <span className="ms-auto h-4 w-4 shrink-0" aria-hidden />
                    )}
                  </button>
                </li>
              );
            })}

            {!loading && filtered.length === 0 ? (
              <li className="px-3 py-4 text-center text-xs text-gray-500 dark:text-slate-400" role="option" aria-selected={false}>
                {safeOptions.length === 0 ? emptyListLabel : noResultsLabel}
              </li>
            ) : null}
          </>
        )}
      </ul>
    </div>
  ) : null;

  return (
    <div ref={containerRef} className={`relative w-full ${disabled ? "opacity-60" : ""}`}>
      {label ? (
        <label id={labelId} htmlFor={id} className={UI_LABEL}>
          {label}
          {required ? <span className="text-red-500"> *</span> : null}
        </label>
      ) : null}

      <div className="relative">
        <button
          ref={triggerRef}
          id={id}
          type="button"
          role="combobox"
          dir={resolvedDir}
          disabled={disabled || loading}
          onClick={() => (open ? close() : openMenu())}
          onKeyDown={onTriggerKeyDown}
          className={triggerClasses}
          aria-expanded={open}
          aria-haspopup="listbox"
          aria-controls={open ? listboxId : undefined}
          aria-labelledby={label ? labelId : undefined}
          aria-invalid={hasError || undefined}
          aria-describedby={
            [hasError ? errorId : null, helperText ? helperId : null].filter(Boolean).join(" ") ||
            undefined
          }
        >
          <span
            className={`min-w-0 flex-1 truncate text-start ${
              showPlaceholder ? "text-gray-400 dark:text-slate-500" : ""
            }`}
          >
            {loading ? loadingLabel : showPlaceholder ? defaultPlaceholder : matched?.label}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {canClear && !isEmptyValue(value) && !disabled && !loading ? (
              <span
                role="button"
                tabIndex={-1}
                className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-white/10 dark:hover:text-slate-200"
                aria-label={isArabic ? "مسح" : "Clear"}
                onClick={(e) => {
                  e.stopPropagation();
                  emit(null);
                }}
              >
                <MdClose className="h-4 w-4" />
              </span>
            ) : null}
            <MdExpandMore
              className={`h-5 w-5 text-gray-400 transition-transform dark:text-slate-500 ${
                open ? "rotate-180" : ""
              }`}
              aria-hidden
            />
          </span>
        </button>

        {!portal && dropdown}
      </div>

      {portal && dropdown ? createPortal(dropdown, document.body) : null}

      {hasError && errorMessage ? (
        <p id={errorId} className="mt-1 text-xs text-red-600 dark:text-red-400" role="alert">
          {errorMessage}
        </p>
      ) : helperText ? (
        <p id={helperId} className="mt-1 text-xs text-gray-500 dark:text-slate-400">
          {helperText}
        </p>
      ) : null}
    </div>
  );
}
